import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import ArticleCard from "@/components/base/ArticleCard";
import StoreCard from "@/components/base/StoreCard";
import {
  getLocationBySlug,
  getSubLocations,
  getPostsByLocationSlug,
  getStoresByLocationSlug,
} from "@/api/content";
import { canonical, collectionPageSchema } from "@/utils/seo";
import type { Location, PostCard, StoreCard } from "@/types/content";

export default function LocationDetailPage({ initialData }: { initialData?: Location | null }) {
  const { slug } = useParams<{ slug: string }>();
  const [location, setLocation] = useState<Location | null>(initialData ?? null);
  const [subLocations, setSubLocations] = useState<Location[]>([]);
  const [posts, setPosts] = useState<PostCard[]>([]);
  const [storeCards, setStoreCards] = useState<StoreCard[]>([]);
  const [activeArea, setActiveArea] = useState<string>("all");
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug) return;
    getLocationBySlug(slug).then((data) => {
      if (data) {
        setLocation(data);
        Promise.all([
          getSubLocations(slug),
          getPostsByLocationSlug(slug),
          getStoresByLocationSlug(slug),
        ]).then(([subs, p, s]) => {
          setSubLocations(subs);
          setPosts(p);
          setStoreCards(s);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  const areas = useMemo(() => {
    const areaSet = new Set<string>();
    for (const s of storeCards) {
      const match = s.location.match(/([^\s]+區)/);
      if (match && match[1]) areaSet.add(match[1]);
    }
    return Array.from(areaSet);
  }, [storeCards]);

  const filteredStores = useMemo(() => {
    if (activeArea === "all") return storeCards;
    return storeCards.filter((s) => s.location.includes(activeArea));
  }, [storeCards, activeArea]);

  const relatedTopics = useMemo(() => {
    const tagCounts = new Map<string, number>();
    for (const p of posts) {
      for (const tag of p.tags) {
        if (/[\u4e00-\u9fa5]*區/.test(tag) || tag.includes("市")) continue;
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      }
    }
    return Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag]) => tag);
  }, [posts]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  if (!location) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          找不到此地區
        </h2>
        <a
          href="/locations"
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          返回城市列表
        </a>
      </div>
    );
  }

  const baseMeta = location.seo || {
    title: `${location.name}｜美好時光指南城市指南`,
    description: location.description,
  };
  const meta = {
    ...baseMeta,
    canonical: baseMeta.canonical || canonical(`/locations/${location.slug}`),
    keywords: baseMeta.keywords || [location.name, "城市指南", "美好時光指南"],
    structuredData: baseMeta.structuredData || collectionPageSchema(baseMeta.title, baseMeta.description || location.description, `/locations/${location.slug}`),
  };

  return (
    <>
      <SEO meta={meta} />
      {/* Hero */}
      <section className="relative h-[360px] md:h-[480px] flex items-center justify-center overflow-hidden">
        <img
          src={location.coverImage}
          alt={location.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-3xl md:text-5xl font-bold mb-3">
            {location.name}
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto">
            {location.description}
          </p>
        </div>
      </section>

      {/* 地圖 */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-6 h-6 flex items-center justify-center text-primary">
              <i className="ri-map-2-line" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800">
              探索 {location.name}
            </h2>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-100 card-shadow">
            <iframe
              title={`${location.name} 地圖`}
              width="100%"
              height="400"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(location.name)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
              className="border-0"
            />
          </div>
        </div>
      </section>

      {/* 子區域導航 */}
      {subLocations.length > 0 && (
        <section className="py-6 bg-surface-alt border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <p className="text-sm text-slate-500 mb-3">
              {location.name} 熱門區域
            </p>
            <div className="flex flex-wrap gap-2">
              {subLocations.map((sub) => (
                <a
                  key={sub.slug}
                  href={`/locations/${sub.slug}`}
                  className="px-4 py-2 bg-white text-slate-700 text-sm rounded-full border border-slate-200 hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
                >
                  {sub.name}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 區域篩選 + 店家 */}
      {storeCards.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center text-primary">
                  <i className="ri-store-2-line" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                  {location.name} 探店店家
                </h2>
              </div>
              {areas.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveArea("all")}
                    className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      activeArea === "all"
                        ? "bg-primary text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    全部
                  </button>
                  {areas.map((area) => (
                    <button
                      type="button"
                      key={area}
                      onClick={() => setActiveArea(area)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                        activeArea === area
                          ? "bg-primary text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredStores.map((store) => (
                <StoreCard
                  key={store.id}
                  store={store}
                  footer={
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      <span className="line-clamp-1">{store.location}</span>
                    </div>
                  }
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 精選文章 */}
      {posts.length > 0 && (
        <section className="py-10 md:py-14 bg-surface-alt">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 flex items-center justify-center text-primary">
                <i className="ri-article-line" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                {location.name} 精選文章
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {posts.map((post) => (
                <ArticleCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 相關主題 */}
      {relatedTopics.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 flex items-center justify-center text-primary">
                <i className="ri-price-tag-3-line" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                {location.name} 熱門主題
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {relatedTopics.map((tag) => (
                <a
                  key={tag}
                  href={`/tags/${encodeURIComponent(tag)}`}
                  className="px-4 py-2 bg-slate-50 text-slate-700 text-sm rounded-full border border-slate-200 hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
                >
                  {tag}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}